/**
 * studio.service.js
 *
 * API calls for the Studio chatbot.
 *
 * Studio chat/analyse are INTERACTIVE and must return synchronously, so they use
 * dedicated REST endpoints (`/studio/chat`, `/studio/analyse`) via lambdaClient —
 * the same synchronous-LLM pattern as `/feasibility` and `/suggestions`.
 * (The async `/agentcore/invoke` job queue is only for long-running report /
 * pipeline jobs and would return a "queued" job id, not a chat reply.)
 *
 *   - studioChatFallback → one-shot chat turn
 *   - studioChatStream   → same call, but yields the message in chunks for a
 *                          natural "typing" effect (client-side simulated)
 *   - studioAnalyse      → requirement analysis
 */

import { lambdaClient } from '../config/http.client';

const MSG_REGEX = /"message"\s*:\s*"((?:\\.|[^"\\])*)"/s;
const FENCE_REGEX = /```(?:json)?/gi;
const DEFAULT_CHAT_FALLBACK =
  'I hit a temporary response formatting issue. Please retry your last message.';
const MODEL_TIMEOUT_REPLY =
  'I hit a temporary model timeout while generating the response. Please retry.';

const extractFirstJsonObject = (text) => {
  if (typeof text !== 'string') return null;
  const src = text.trim();
  if (!src) return null;

  for (let i = 0; i < src.length; i += 1) {
    if (src[i] !== '{') continue;

    let depth = 0;
    let inString = false;
    let escape = false;

    for (let j = i; j < src.length; j += 1) {
      const ch = src[j];

      if (inString) {
        if (escape) {
          escape = false;
          continue;
        }
        if (ch === '\\') {
          escape = true;
          continue;
        }
        if (ch === '"') {
          inString = false;
        }
        continue;
      }

      if (ch === '"') {
        inString = true;
        continue;
      }
      if (ch === '{') {
        depth += 1;
        continue;
      }
      if (ch === '}') {
        depth -= 1;
        if (depth === 0) {
          const candidate = src.slice(i, j + 1);
          try {
            return JSON.parse(candidate);
          } catch {
            break;
          }
        }
      }
    }
  }

  return null;
};

const sanitizeMessageText = (text) => {
  if (typeof text !== 'string') return '';
  const trimmed = text.trim();
  if (!trimmed) return '';

  const unfenced = trimmed.replace(FENCE_REGEX, '').trim();

  // If model returned object-like content, extract first valid JSON object and read .message.
  const parsed = extractFirstJsonObject(trimmed) || extractFirstJsonObject(unfenced);
  if (parsed && typeof parsed.message === 'string' && parsed.message.trim()) {
    return parsed.message.trim();
  }

  // Regex salvage for malformed JSON-like blobs.
  const match = trimmed.match(MSG_REGEX) || unfenced.match(MSG_REGEX);
  if (match?.[1]) {
    try {
      const msg = JSON.parse(`"${match[1]}"`);
      if (typeof msg === 'string' && msg.trim()) return msg.trim();
    } catch {
      const msg = match[1].replace(/\\n/g, '\n').replace(/\\"/g, '"').trim();
      if (msg) return msg;
    }
  }

  // Guard against fence-only artifacts like "```".
  if (['', '{', '}', 'json'].includes(unfenced.toLowerCase())) {
    return '';
  }

  // Avoid rendering truncated object blobs.
  if (unfenced.startsWith('{') || unfenced.startsWith('[')) {
    return '';
  }

  return unfenced;
};

const normalizeStudioPayload = (data) => {
  // Some gateways/wrappers may nest actual payload in `body`.
  let payload = data;
  if (payload && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'body')) {
    if (typeof payload.body === 'string') {
      try {
        payload = JSON.parse(payload.body);
      } catch {
        payload = { success: false, error: payload.body };
      }
    } else if (payload.body && typeof payload.body === 'object') {
      payload = payload.body;
    }
  }

  if (payload && typeof payload.message === 'string') {
    const cleaned = sanitizeMessageText(payload.message);
    payload = { ...payload, message: cleaned || DEFAULT_CHAT_FALLBACK };
  }

  return payload || {};
};

// ── Non-streaming chat turn ────────────────────────────────────────────────

/**
 * Send a chat message and receive the full response in one shot.
 * @returns {Promise<{message, gathered, topics_remaining, suggested_name, is_complete}>}
 */
export const studioChatFallback = async ({ messages, gathered, modelId, projectId, phaseContext = {}, phaseHistory = {} }) => {
  const payload = {
    messages,
    gathered,
    phase_context: phaseContext,
    phase_history: phaseHistory,
    model_id: modelId,
    project_id: projectId,
    runtimeSessionId: projectId,
  };

  const postChatOnce = async () => {
    const res = await lambdaClient.post('/studio/chat', payload);
    return normalizeStudioPayload(res.data);
  };

  const first = await postChatOnce();
  if (String(first?.message || '').trim() !== MODEL_TIMEOUT_REPLY) {
    return first;
  }

  // Recover common transient model timeouts with one immediate retry.
  await new Promise((r) => setTimeout(r, 350));
  const second = await postChatOnce();
  return second || first;
};

// ── Streaming chat (client-side simulated) ─────────────────────────────────

/**
 * Async generator that yields the agent's response in small chunks so the UI
 * can render a natural typing effect. Under the hood it makes a single
 * request/response call, then trickles the resulting message word-by-word.
 *
 * Usage:
 *   for await (const event of studioChatStream({...})) {
 *     if (event.token)  appendToken(event.token);
 *     if (event.done)   handleComplete(event.meta);   // meta = {gathered, topics_remaining, ...}
 *     if (event.error)  handleError(event.error);
 *   }
 */
export async function* studioChatStream({ messages, gathered, modelId, projectId, phaseContext = {}, phaseHistory = {} }) {
  let data;
  try {
    data = await studioChatFallback({ messages, gathered, modelId, projectId, phaseContext, phaseHistory });
  } catch (err) {
    yield { error: err?.response?.data?.error || err.message || 'Request failed' };
    return;
  }

  if (data && data.success === false) {
    yield { error: data.error || 'Studio chat failed' };
    return;
  }

  const message = data.message || '';
  const responseSource = String(data.response_source || '').toLowerCase();
  const shouldSimulateTyping = responseSource === 'model' && message.length >= 220;

  if (!shouldSimulateTyping) {
    if (message) {
      yield { token: message };
    }
  } else {
    const wordChunks = message.match(/\S+\s*/g) || [message];
    const groupSize = message.length > 1200 ? 6 : 4;
    const delayMs = message.length > 1200 ? 8 : 12;

    for (let i = 0; i < wordChunks.length; i += groupSize) {
      const token = wordChunks.slice(i, i + groupSize).join('');
      yield { token };
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }

  const { message: _omit, ...meta } = data;
  yield { done: true, meta };
}

// ── Requirement analysis ───────────────────────────────────────────────────

/**
 * Validate and summarise gathered requirements.
 * @returns {Promise<{valid, score, reasons, gaps, summary}>}
 */
export const studioAnalyse = async ({ gathered, modelId, projectId, phaseContext = {}, phaseHistory = {} }) => {
  const res = await lambdaClient.post('/studio/analyse', {
    gathered,
    phase_context: phaseContext,
    phase_history: phaseHistory,
    model_id: modelId,
    project_id: projectId,
    runtimeSessionId: projectId,
  });
  const normalized = normalizeStudioPayload(res.data);

  // Ensure expected analysis shape so UI does not break on partial responses.
  const summary = normalized.summary && typeof normalized.summary === 'object'
    ? normalized.summary
    : {};

  const techStack = summary.tech_stack && typeof summary.tech_stack === 'object'
    ? summary.tech_stack
    : {};

  const inferredCloud = techStack.cloud || summary.cloud_provider || null;
  const mergedTechStack = {
    ...techStack,
    ...(inferredCloud ? { cloud: inferredCloud } : {}),
  };

  const mergedSummary = {
    ...summary,
    tech_stack: mergedTechStack,
  };

  return {
    valid: Boolean(normalized.valid),
    score: Number(normalized.score || 0),
    reasons: Array.isArray(normalized.reasons) ? normalized.reasons : [],
    gaps: Array.isArray(normalized.gaps) ? normalized.gaps : [],
    summary: mergedSummary,
    requirement_document: normalized.requirement_document || null,
    design_document: normalized.design_document || null,
    documentation_bundle: normalized.documentation_bundle || null,
    phase_context: normalized.phase_context || phaseContext,
    current_phase: normalized.current_phase || (phaseContext.current_phase || 'requirements'),
  };
};

/**
 * Fetch the phase-appropriate opening message from the backend.
 * Falls back to deterministic local text so new chats always have an opening prompt.
 * @param {string} phase  'requirements' | 'design' | 'documentation'
 */
const STUDIO_OPENING_FALLBACK = {
  requirements:
    "Hi! I'm your SDLC Requirements Consultant. Share your idea, users, goals, functional and non-functional requirements, scale, timeline, budget, integrations, and constraints. You can paste a full PRD and I'll structure it.",
  design:
    "We are in Design Mode. Share your HLD/LLD, APIs, data flow, schema, security design, deployment topology, and constraints. You can paste the full design document.",
  documentation:
    "We are in Documentation Mode. Share audience, scope, API/frontend/backend docs, deployment and operations runbooks, and known limitations. You can paste existing documentation and I'll structure it.",
};

export const studioGetOpening = async (phase = 'requirements') => {
  const query = `phase=${encodeURIComponent(phase)}`;
  const candidatePaths = [
    `/studio/opening?${query}`,
    `/api/studio/opening?${query}`,
  ];

  for (const path of candidatePaths) {
    try {
      const res = await lambdaClient.get(path);
      const message = typeof res.data?.message === 'string' ? res.data.message.trim() : '';
      if (message) return message;
    } catch {
      // Try next candidate path.
    }
  }

  return STUDIO_OPENING_FALLBACK[phase] || STUDIO_OPENING_FALLBACK.requirements;
};
